import { EditNetworkQuestionsSettings, NetworkQuestionsEnablementMode } from '@ms/yammer-graphql';
import mutationEditNetworkQuestionsSettings from '@ms/yammer-graphql/dist/operations/mutationEditNetworkQuestionsSettings.graphql';

import { ManagedApiLifecycle, getGraphqlApiRequest, managedApiAction } from '../..';
import { reportError } from '../../telemetry';
import { PromiseThunkAction } from '../../types';

import {
  EditNetworkQuestionSettingRejectedPayload,
  EditNetworkQuestionSettingsFulfilledAction,
  EditNetworkQuestionSettingsPendingAction,
  EditNetworkQuestionSettingsRejectedAction,
} from './editNetworkQuestionSettingsActions.types';
import { getNetworkQuestionsEnablementMode } from './selectors';

interface NetworkQuestionSettingsResponse {
  readonly data: EditNetworkQuestionsSettings;
}

interface EditNetworkQuestionSettingsProps {
  readonly networkQuestionsMode: NetworkQuestionsEnablementMode;
  readonly isGroupQuestionsAIImporterEnabled?: boolean;
  readonly isNetworkQuestionsAIFeaturesEnabled?: boolean;
  readonly isNetworkQuestionsAIImporterEnabled?: boolean;
}

export enum NetworkQuestionSettingsKeys {
  networkQuestionsEnablementMode = 'networkQuestionsEnablementMode',
}

type EditNetworkQuestionSettingsPending = (
  props: EditNetworkQuestionSettingsProps
) => EditNetworkQuestionSettingsPendingAction;
const editNetworkQuestionSettingsPending: EditNetworkQuestionSettingsPending = ({
  networkQuestionsMode,
  isGroupQuestionsAIImporterEnabled,
  isNetworkQuestionsAIFeaturesEnabled,
  isNetworkQuestionsAIImporterEnabled,
}) => ({
  type: 'NETWORK_QUESTION_SETTINGS.EDIT_PENDING',
  networkQuestionsMode,
  isGroupQuestionsAIImporterEnabled,
  isNetworkQuestionsAIFeaturesEnabled,
  isNetworkQuestionsAIImporterEnabled,
});

type EditNetworkQuestionSettingsFulfilled = (
  payload: EditNetworkQuestionSettingsFulfilledAction['payload'],
  props: EditNetworkQuestionSettingsProps
) => EditNetworkQuestionSettingsFulfilledAction;
const editNetworkQuestionSettingsFulfilled: EditNetworkQuestionSettingsFulfilled = (
  payload,
  {
    networkQuestionsMode,
    isGroupQuestionsAIImporterEnabled,
    isNetworkQuestionsAIFeaturesEnabled,
    isNetworkQuestionsAIImporterEnabled,
  }
): EditNetworkQuestionSettingsFulfilledAction => ({
  type: 'NETWORK_QUESTION_SETTINGS.EDIT_FULFILLED',
  payload,
  networkQuestionsMode,
  isGroupQuestionsAIImporterEnabled,
  isNetworkQuestionsAIFeaturesEnabled,
  isNetworkQuestionsAIImporterEnabled,
});

const editNetworkQuestionSettingsRejected = (
  payload: EditNetworkQuestionSettingRejectedPayload
): EditNetworkQuestionSettingsRejectedAction => ({
  type: 'NETWORK_QUESTION_SETTINGS.EDIT_REJECTED',
  payload,
});

const editNetworkQuestionSettingsRequest = ({
  networkQuestionsMode,
  isGroupQuestionsAIImporterEnabled,
  isNetworkQuestionsAIFeaturesEnabled,
  isNetworkQuestionsAIImporterEnabled,
}: EditNetworkQuestionSettingsProps) => {
  const input = {
    networkQuestionsEnablementMode: networkQuestionsMode,
    ...(isGroupQuestionsAIImporterEnabled !== undefined && { isGroupQuestionsAIImporterEnabled }),
    ...(isNetworkQuestionsAIFeaturesEnabled !== undefined && { isNetworkQuestionsAIFeaturesEnabled }),
    ...(isNetworkQuestionsAIImporterEnabled !== undefined && { isNetworkQuestionsAIImporterEnabled }),
  };

  return getGraphqlApiRequest({
    body: {
      operationName: 'EditNetworkQuestionsSettings',
      query: mutationEditNetworkQuestionsSettings,
      variables: {
        input,
      },
    },
  });
};

const editNetworkQuestionSettingsLifecycle = ({
  networkQuestionsMode,
  prevSettingValue,
  isGroupQuestionsAIImporterEnabled,
  isNetworkQuestionsAIFeaturesEnabled,
  isNetworkQuestionsAIImporterEnabled,
}: EditNetworkQuestionSettingsProps & {
  readonly prevSettingValue: NetworkQuestionsEnablementMode;
}): ManagedApiLifecycle<NetworkQuestionSettingsResponse> => ({
  pending: () =>
    editNetworkQuestionSettingsPending({
      networkQuestionsMode,
      isGroupQuestionsAIImporterEnabled,
      isNetworkQuestionsAIFeaturesEnabled,
      isNetworkQuestionsAIImporterEnabled,
    }),
  fulfilled: (rawNetworkQuestionSettings) =>
    editNetworkQuestionSettingsFulfilled(rawNetworkQuestionSettings.data, {
      networkQuestionsMode,
      isGroupQuestionsAIImporterEnabled,
      isNetworkQuestionsAIFeaturesEnabled,
      isNetworkQuestionsAIImporterEnabled,
    }),
  rejected: () => editNetworkQuestionSettingsRejected({ prevSettingValue }),
});

type EditNetworkQuestionSettings = (props: EditNetworkQuestionSettingsProps) => PromiseThunkAction<void>;
export const editNetworkQuestionSettings: EditNetworkQuestionSettings =
  ({
    networkQuestionsMode,
    isGroupQuestionsAIImporterEnabled,
    isNetworkQuestionsAIFeaturesEnabled,
    isNetworkQuestionsAIImporterEnabled,
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const prevSettingValue = getNetworkQuestionsEnablementMode(state) || NetworkQuestionsEnablementMode.DISABLED;
    const request = editNetworkQuestionSettingsRequest({
      networkQuestionsMode,
      isGroupQuestionsAIImporterEnabled,
      isNetworkQuestionsAIFeaturesEnabled,
      isNetworkQuestionsAIImporterEnabled,
    });
    const lifecycle = editNetworkQuestionSettingsLifecycle({
      networkQuestionsMode,
      prevSettingValue,
      isGroupQuestionsAIImporterEnabled,
      isNetworkQuestionsAIFeaturesEnabled,
      isNetworkQuestionsAIImporterEnabled,
    });
    const action = managedApiAction({ request, lifecycle });

    try {
      await dispatch(action);
    } catch (error) {
      reportError({ error });
    }
  };
